import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { usePlus } from '../context/PlusContext';
import { X } from 'lucide-react';
import { Categoria } from '../types';
import { IconPicker } from './IconPicker';
import { useCerrarConEscape } from '../hooks/useCerrarConEscape';

interface ModalAgregarCategoriaProps {
  onClose: () => void;
  categoriaEditar?: Categoria;
}

export const ModalAgregarCategoria: React.FC<ModalAgregarCategoriaProps> = ({ onClose, categoriaEditar }) => {
  useCerrarConEscape(onClose);
  const { crearCategoria, actualizarCategoria } = useData();
  const { ofrecerSiEsLimite } = usePlus();
  const modoEdicion = !!categoriaEditar;

  const [nombre, setNombre] = useState(categoriaEditar?.nombre || '');
  const [tipo, setTipo]     = useState<'ingreso' | 'gasto'>(categoriaEditar?.tipo || 'gasto');
  const [color, setColor]   = useState(categoriaEditar?.color || '#3B82F6');
  const [icono, setIcono]   = useState(categoriaEditar?.icono || '📊');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const colores = ['#3B82F6','#EF4444','#10B981','#F59E0B','#8B5CF6','#EC4899','#06B6D4','#14B8A6','#F97316','#84CC16','#6366F1','#D946EF'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!nombre) { setError('Por favor ingresa un nombre'); return; }
    setLoading(true);
    try {
      if (modoEdicion && categoriaEditar) {
        await actualizarCategoria(categoriaEditar.id, { nombre, tipo, color, icono });
      } else {
        await crearCategoria({ nombre, tipo, color, icono });
      }
      onClose();
    } catch (err: any) {
      // Sin hueco en el plan gratis: en vez del error, la hoja de Plus
      if (ofrecerSiEsLimite(err)) { onClose(); return; }
      setError(err.message || 'Error al guardar categoría');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center capa-modal z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-full overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {modoEdicion ? 'Editar Categoría' : 'Nueva Categoría'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="boton-cerrar text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}

          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Nombre</label>
            <input type="text" value={nombre} onChange={e => setNombre(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Ej: Comida" required />
          </div>

          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Tipo</label>
            <div className="flex gap-4">
              {(['ingreso', 'gasto'] as const).map(t => (
                <label key={t} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" value={t} checked={tipo === t}
                    onChange={() => setTipo(t)} className="accent-blue-600" />
                  <span className={`font-semibold ${t === 'ingreso' ? 'text-green-600' : 'text-red-600'}`}>
                    {t === 'ingreso' ? 'Ingreso' : 'Gasto'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Icono</label>
            <IconPicker value={icono} onChange={setIcono} />
          </div>

          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Color</label>
            <div className="flex flex-wrap gap-2">
              {colores.map(col => (
                <button key={col} type="button" onClick={() => setColor(col)}
                  className={`w-8 h-8 rounded-lg transition ${color === col ? 'ring-2 ring-offset-2 ring-gray-500 dark:ring-offset-gray-800' : ''}`}
                  style={{ backgroundColor: col }} />
              ))}
            </div>
          </div>

          <div className="flex gap-4">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:opacity-50">
              {loading ? 'Guardando...' : modoEdicion ? 'Actualizar' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
